import customFetch from "../util/config/axiosConfig";
import { ApiResponse } from "../types/responseType";
import { toast } from "sonner";
import {
  AddStaffForm,
  Staff,
  StaffFilterParams,
  StaffListResponse,
} from "../types/staff";
import { Subjects } from "../types/schoolBranchType";

const StaffApis = {
  //post
  addStaff: async (
    staff: AddStaffForm,
  ): Promise<ApiResponse<AddStaffForm> | undefined> => {
    try {
      const response = await customFetch.post("/api/v1/staff/add", {
        fullName: staff.fullName,
        email: staff.email,
        mobile: staff.mobile,
        roleName: staff.roleName,
        employeeCode: staff.employeeCode,
        password: staff.password,
        dateOfBirth: staff.dateOfBirth,
        dateOfJoining: staff.dateOfJoining,
        gender: staff.gender?.toUpperCase() as "MALE" | "FEMALE",
        qualification: staff.qualification,
        about: staff.about,
        branchCode: staff.branchCode,
        active: true,
      });

      return response.data;
    } catch (error) {
      console.error("Error in addStaff:", error);
      toast.error("Failed to add staff");
    }
  },

  // GET Staff
  getStaff: async (
    params: StaffFilterParams,
  ): Promise<StaffListResponse | undefined> => {
    try {
      // REMOVE empty values
      const cleanedParams = Object.fromEntries(
        Object.entries(params).filter(
          ([_, value]) => value !== "" && value !== null && value !== undefined,
        ),
      );

      const response = await customFetch.get("/api/v1/staff/filter", {
        params: cleanedParams,
      });

      return response.data;
    } catch (error) {
      console.error("Error fetching staff:", error);
      toast.error("Failed to fetch staff list");
    }
  },

  // PUT Update staff
  updateStaff: async (
    id: number,
    staffData: Staff,
  ): Promise<ApiResponse<Staff> | undefined> => {
    try {
      const response = await customFetch.put(
        `/api/v1/staff/update/${id}`,
        staffData,
      );

      return response.data;
    } catch (error) {
      console.error("Error updating staff:", error);
      throw error;
    }
  },

  getStaffSubjects: async (
    staffId: number,
  ): Promise<ApiResponse<Subjects[]> | undefined> => {
    try {
      const response = await customFetch.get(
        `/api/v1/staff/subjects/${staffId}`,
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching staff subjects:", error);
      toast.error("Failed to fetch subjects");
    }
  },

  // PATCH Toggle subject
  toggleStaffSubject: async (
    staffId: number,
    subjectId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch(
        `/api/v1/staff/toggle-subject/${staffId}`,
        subjectId,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error toggling subject:", error);
      toast.error("Failed to toggle subject");
    }
  },

  // PATCH Toggle Academic Cycle
  toggleAcademicCycle: async (
    staffId: number,
    academicCycleId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch(
        `/api/v1/staff/assign-academic-cycle/${staffId}`,
        academicCycleId,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error toggling subject:", error);
      toast.error("Failed to toggle Academic Cycle");
    }
  },

  // PATCH Toggle features
  toggleFeature: async (
    staffId: number,
    featureId: number,
    canEdit: boolean,
    active: boolean,
  ) => {
    const response = await customFetch.patch(
      `/api/v1/staff/assign-feature/${staffId}?canEdit=${canEdit}&active=${active}`,
      featureId,
      {
        headers: {
          "Content-Type": "application/json",
        },
      },
    );

    return response.data;
  },

  // PATCH Activate/de-activate
  toggleActivate: async (
    staffId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch(
        `/api/v1/staff/toggle-active/${staffId}`,
        {},
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error toggling active status:", error);
      throw error;
    }
  },

  // Soft delete
  deleteStaff: async (
    staffId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch(
        `/api/v1/staff/toggle-soft-delete/${staffId}`,
        {
          headers: {
            "Content-Type": "application/json",
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error("Error toggling subject:", error);
      toast.error("Failed to toggle Academic Cycle");
    }
  },

  // by id
  getStaffById: async (
    staffId: number,
  ): Promise<ApiResponse<Staff> | undefined> => {
    try {
      const response = await customFetch.get<ApiResponse<Staff>>(
        `/api/v1/staff/${staffId}`,
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching staff by id:", error);
      toast.error("Failed to fetch staff details");
    }
  },

  // GET Staff Roles
  getStaffRoles: async (): Promise<ApiResponse<string[]> | undefined> => {
    try {
      const response = await customFetch.get<ApiResponse<string[]>>(
        "/api/v1/staff/roles",
      );

      return response.data;
    } catch (error) {
      console.error("Error fetching staff roles:", error);
      toast.error("Failed to fetch roles");
    }
  },

  changeStaffProfile: async (
    staffId: number,
    staffProfile: string,
  ): Promise<ApiResponse<string>> => {
    const response = await customFetch.patch(
      `/api/v1/staff/update-profile/${staffId}`,
      staffProfile,
      {
        headers: {
          "Content-Type": "text/plain",
        },
      },
    );

    return response.data;
  },

  // PATCH Verify Finance
  verifyFinance: async (
    staffId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch<ApiResponse<string>>(
        `/api/v1/staff/verify-finance/${staffId}`,
        {},
      );

      toast.success(response.data.data || "Finance verified successfully");
      return response.data;
    } catch (error: any) {
      console.error("Error verifying finance:", error);
      toast.error(error?.response?.data?.message || "Failed to verify finance");
      throw error;
    }
  },

  // PATCH Verify Document
  verifyDocument: async (
    staffId: number,
  ): Promise<ApiResponse<string> | undefined> => {
    try {
      const response = await customFetch.patch<ApiResponse<string>>(
        `/api/v1/staff/verify-documents/${staffId}`,
        {},
      );

      toast.success(response.data.data || "Document verified successfully");
      return response.data;
    } catch (error: any) {
      console.error("Error verifying Document:", error);
      toast.error(error?.response?.data?.message || "Failed to verify Document");
      throw error;
    }
  },

};

export default StaffApis;
